import { useContext, useCallback, createElement as _createElement } from 'react';
import { getFirstItem, getSecondItem, getBandwidth } from '@visx/shape';
import DataContext from "../../../context/DataContext.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { getStackValue } from "../../../utils/combineBarStackData.js";
import { BARSTACK_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import findNearestStackDatum from "../../../utils/findNearestStackDatum.js";
import useStackedData from "../../../hooks/useStackedData.js";
import { jsx as _jsx } from "react/jsx-runtime";
function BaseBarStack(_ref) {
  let {
    children,
    order,
    offset,
    BarsComponent,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true
  } = _ref;
  const {
    colorScale,
    dataRegistry,
    horizontal,
    xScale,
    yScale
  } = useContext(DataContext);
  const {
    seriesChildren,
    dataKeys,
    stackedData
  } = useStackedData({
    children,
    order,
    offset
  });

  // custom logic to find the nearest AreaStackDatum (context) and return the original Datum (props)
  const findNearestDatum = useCallback(params => {
    const childData = seriesChildren.find(child => child.props.dataKey === params.dataKey)?.props?.data;
    return childData ? findNearestStackDatum(params, childData, horizontal) : null;
  }, [seriesChildren, horizontal]);
  const ownEventSourceKey = `${BARSTACK_EVENT_SOURCE}-${dataKeys.join('-')}`;
  const eventEmitters = useSeriesEvents({
    dataKey: dataKeys,
    enableEvents,
    // @ts-expect-error Datum input + return type are expected to be the same type but they differ for BarStack (registry data is StackedDatum, return type is user Datum)
    findNearestDatum,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });
  const registryEntries = dataKeys.map(key => dataRegistry.get(key));

  // if scales and data are not available in the registry, bail
  if (registryEntries.some(entry => entry == null) || !xScale || !yScale || !colorScale) {
    return null;
  }
  const barThickness = getBandwidth(horizontal ? yScale : xScale);
  const halfBarThickness = barThickness / 2;
  let getWidth;
  let getHeight;
  let getX;
  let getY;
  if (horizontal) {
    getWidth = bar => (xScale(getSecondItem(bar)) ?? NaN) - (xScale(getFirstItem(bar)) ?? NaN);
    getHeight = () => barThickness;
    getX = bar => xScale(getFirstItem(bar));
    getY = bar => 'bandwidth' in yScale ? yScale(getStackValue(bar.data)) : Math.max((yScale(getStackValue(bar.data)) ?? NaN) - halfBarThickness);
  } else {
    getWidth = () => barThickness;
    getHeight = bar => (yScale(getFirstItem(bar)) ?? NaN) - (yScale(getSecondItem(bar)) ?? NaN);
    getX = bar => 'bandwidth' in xScale ? xScale(getStackValue(bar.data)) : Math.max((xScale(getStackValue(bar.data)) ?? NaN) - halfBarThickness);
    getY = bar => yScale(getSecondItem(bar));
  }
  const barSeries = stackedData.map((barStack, stackIndex) => {
    const entry = dataRegistry.get(barStack.key);
    if (!entry) return null;

    // get props from child BarSeries, if available
    const childBarSeries = seriesChildren.find(child => child.props.dataKey === barStack.key);
    const {
      colorAccessor,
      radius,
      radiusAll,
      radiusBottom,
      radiusLeft,
      radiusRight,
      radiusTop
    } = childBarSeries?.props || {};
    return {
      key: barStack.key,
      radius,
      radiusAll,
      radiusBottom,
      radiusLeft,
      radiusRight,
      radiusTop,
      bars: barStack.map((bar, index) => {
        const barX = getX(bar);
        if (!isValidNumber(barX)) return null;
        const barY = getY(bar);
        if (!isValidNumber(barY)) return null;
        const barWidth = getWidth(bar);
        if (!isValidNumber(barWidth)) return null;
        const barHeight = getHeight(bar);
        if (!isValidNumber(barHeight)) return null;
        const barSeriesDatum = colorAccessor ? childBarSeries?.props?.data[index] : null;
        return {
          key: `${stackIndex}-${barStack.key}-${index}`,
          x: barX,
          y: barY,
          width: barWidth,
          height: barHeight,
          fill: barSeriesDatum && colorAccessor ? colorAccessor(barSeriesDatum, index) : colorScale(barStack.key)
        };
      }).filter(bar => bar)
    };
  }).filter(series => series);
  return /*#__PURE__*/_jsx("g", {
    className: "visx-bar-stack",
    children: barSeries.map(series => series && /*#__PURE__*/_createElement(BarsComponent, {
      horizontal: horizontal,
      xScale: xScale,
      yScale: yScale,
      ...series,
      ...eventEmitters,
      key: series.key
    }))
  });
}
export default BaseBarStack;